import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { SectionHeader } from '../../common/SharedComponents';
import { applicationInsights } from '../../ApplicationInsights';
import { formatDate } from '../../common/SharedFunctions';
import { BfiDataGrid, Column } from '../../common/SharedComponents';
import { GridSettingType } from '@apply/constants';
import { CellDblClickEvent } from 'devextreme/ui/data_grid';
import { EditGeneralContractorModal } from './components/EditGeneralContractorModal';
import { GeneralContractorActionsButton } from '../common/components/GeneralContractorActionsButton';
import { useGeneralContractorQuery, useGeneralContractorsQuery } from './queries/generalContractorQueries';
import { useGeneralContractorStore } from './stores/generalContractorStore';
import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { useDispatch } from 'react-redux';
import { searchActions } from '@apply/adminStore';
import { debounce } from 'lodash';
import { DataGrid } from 'devextreme-react';

export const GeneralContractorUsersSummary = () => {
    const gridRef = useRef<DataGrid>(null);
    const dispatch = useDispatch();
    const debouncedSearch = debounce(dispatch, 100);
    const searchState = useSelector((state) => state.searchState);

    const { set_modalContractor } = useGeneralContractorStore();
    const [modalContractorId, set_modalContractorId] = useState<number | undefined>(undefined);

    const { data: generalContractor } = useGeneralContractorQuery(modalContractorId);
    const { data: generalContractors } = useGeneralContractorsQuery(searchState.searchText?.generalContractorUser);

    useEffect(() => {
        if (!generalContractor) return;

        set_modalContractor(generalContractor);
        set_modalContractorId(undefined);
    }, [generalContractor]);

    const onCellDblClick = async (e: CellDblClickEvent) => {
        if (!e?.data?.id) return;

        set_modalContractorId(e.data.id);
    };

    const _onSearchChange = (event: { target: { value: unknown } }) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (debouncedSearch as any)(
            searchActions.setSearchText({
                searchText: {
                    ...searchState.searchText,
                    generalContractorUser: event.target.value,
                },
            })
        );
    };

    // Forces a Refresh of the BFIDataGrid
    // Required because we are using DataSource and Not DataStore
    // Filtered Search results were showing as stale after removing filter
    useEffect(() => {
        gridRef.current?.instance.repaint();
    }, [generalContractors]);

    return (
        <>
            <SectionHeader title='Manage General Contractors' />
            <div
                className='main-content'
                style={{ padding: 0, backgroundColor: '#f4f4f4' }}
            >
                <div className='input-has-icon main-search'>
                    <input
                        type='search'
                        placeholder='Search to Filter Users'
                        value={searchState.searchText?.generalContractorUser}
                        onChange={_onSearchChange}
                    />
                    <FontAwesomeIcon
                        icon={faSearch}
                        aria-hidden='true'
                        className='icon'
                    />
                </div>
                <div className='scrollable-content'>
                    <div
                        className='accordion'
                        data-accordion=''
                        data-allow-all-closed='true'
                        data-multi-expand='true'
                    >
                        <BfiDataGrid
                            keyExpr='id'
                            dataGridRef={gridRef}
                            gridType={GridSettingType.GeneralContractorUsersTable}
                            dataSource={generalContractors}
                            applicationInsights={applicationInsights}
                            onCellDblClick={onCellDblClick}
                        >
                            <Column
                                dataField='firstName'
                                caption='First'
                                dataType='string'
                                width={120}
                            />
                            <Column
                                dataField='lastName'
                                caption='Last'
                                dataType='string'
                                width={120}
                            />
                            <Column
                                dataField='email'
                                caption='Email'
                                dataType='string'
                                width={220}
                            />
                            <Column
                                dataField='userRole'
                                caption='Role'
                                dataType='string'
                                width={220}
                            />
                            <Column
                                dataField='created'
                                caption='Created'
                                cellRender={(e) => formatDate(e.data.created)}
                                width={120}
                            />
                            <Column
                                dataField='lastActive'
                                caption='Last Active'
                                cellRender={(e) => formatDate(e.data.lastActive)}
                                width={120}
                            />
                            <Column
                                dataField='lastLoginIPAddress'
                                caption='Last Login IP'
                                dataType='string'
                                width={100}
                            />
                            <Column
                                dataField='lastPasswordResetRequested'
                                caption='Last Pwd Requested'
                                cellRender={(e) => formatDate(e.data.lastPasswordResetRequested)}
                                width={140}
                            />
                            <Column
                                dataField='lastPasswordResetSent'
                                caption='Last Pwd Reset Sent'
                                cellRender={(e) => formatDate(e.data.lastPasswordResetSent)}
                                width={140}
                            />
                            <Column
                                dataField='lastFailedLoginAttempt'
                                caption='Last Failed Login'
                                cellRender={(e) => formatDate(e.data.lastFailedLoginAttempt)}
                                width={120}
                            />
                            <Column
                                alignment='center'
                                cellRender={(e) => <GeneralContractorActionsButton contractorId={e.data.id} />}
                                width={50}
                                allowSorting={false}
                                allowResizing={false}
                                type='actions'
                                fixed={true}
                                fixedPosition={'right'}
                            />
                        </BfiDataGrid>
                        <EditGeneralContractorModal />
                    </div>
                </div>
            </div>
        </>
    );
};
